import React, { forwardRef } from 'react'
import styled from '@emotion/styled'

import { Icons } from '../../theme/icons'
import { Box, BoxProps } from '../Box'
import { useTheme } from '../../hooks'

export interface IconProps extends BoxProps {
  /**
   * The name of the icon.
   */
  name?: Icons | string
  /**
   * The color of the icon.
   */
  color?: string
  /**
   * The role of the icon. `presentation` or `img`
   */
  role?: 'presentation' | 'img'
  /**
   * If `false`, it means the icon is used within interactive
   * element and won't be focuable.
   */
  focusable?: boolean
}

const Svg = styled(Box)`
  flex-shrink: 0;
  backface-visibility: hidden;
  &:not(:root) {
    overflow: hidden;
  }
`

export const Icon = forwardRef(
  (
    {
      size = '1em',
      name,
      color = 'currentColor',
      role = 'presentation',
      focusable = false,
      ...rest
    }: IconProps,
    ref,
  ) => {
    const { icons: iconPaths } = useTheme()

    // Fallback in case you pass the wrong name
    const iconFallback = iconPaths['question-outline']

    const path = iconPaths[name] == null ? iconFallback.path : iconPaths[name].path

    const viewBox =
      (iconPaths[name] == null ? iconFallback.viewBox : iconPaths[name].viewBox) || '0 0 24 24'

    const _props = { viewBox, focusable }

    return (
      <Svg
        ref={ref}
        as="svg"
        size={size}
        color={color}
        display="inline-block"
        verticalAlign="middle"
        role={role}
        {..._props}
        {...rest}
      >
        {path}
      </Svg>
    )
  },
)

Icon.displayName = 'Icon'

export default Icon
